import React from 'react';
import { Radio } from 'antd';
import { useTranslation } from 'react-i18next';
import LanguageSelect from './LanguageSelect';
import HideStreetNumberSelect from "./HideStreetNumberSelect";
import AdditionalPDFSelect from "./AdditionalPDFSelect";

/**
 * PdfSettingsForm - Reusable component for PDF settings configuration
 * @param {string} productType - Selected product type
 * @param {function} setProductType - Function to set product type
 * @param {string} rewardVisibility - Selected reward visibility option
 * @param {function} setRewardVisibility - Function to set reward visibility
 * @param {string} selectedLanguage - Selected language
 * @param {function} setSelectedLanguage - Function to set language
 * @param {boolean} hideStreetNumber - Show street number in address
 * @param {function} setHideStreetNumber - Function to set visible of the street number in address
 * @param {null} additionalPDF - Add additional PDF to email
 * @param {function} setAdditionalPDF - Function to set additional PDF to email
 * @param {boolean} showAutomaticLanguage - Whether to show automatic language option
 * @param {boolean} showAutomaticProductType - Whether to show automatic product type options
 * @param {Array} customProductTypeOptions - Custom product type options (array of values)
 * @param {Array} customRewardVisibilityOptions - Custom reward visibility options (array of values)
 * @param {boolean} showProductTypeSection - Whether to show product type section (default: true)
 * @param {boolean} showRewardVisibilitySection - Whether to show reward visibility section (default: true)
 * @param {boolean} showLanguageSection - Whether to show language section (default: true)
 * @param {boolean} showStreetNumberSection - Whether to show street number section (default: false)
 * @param {boolean} showAdditionalPDFSection - Whether to show additional PDF section (default: false)
 */
const PdfSettingsForm = ({
  productType,
  setProductType,
  rewardVisibility,
  setRewardVisibility,
  selectedLanguage,
  setSelectedLanguage,
  setHideStreetNumber,
  setAdditionalPDF,
  showAutomaticLanguage = false,
  showAutomaticProductType = false,
  customProductTypeOptions = null,
  customRewardVisibilityOptions = null,
  showProductTypeSection = true,
  showRewardVisibilitySection = true,
  showLanguageSection = true,
  hideStreetNumber = true,
  additionalPDF = null,
  showStreetNumberSection = false,
  showAdditionalPDFSection = false,
}) => {
  const { t } = useTranslation();

  const defaultProductTypeOptions = ["apartment", "office"];
  const defaultRewardVisibilityOptions = ["show", "hide"];

  const buildingSetupProductTypeOptions = ["automatic", "apartment_buildings", "commercial_spaces"];
  const buildingSetupRewardVisibilityOptions = ["total", "separate", "none", "with_substitute_reward"];

  const productTypeOptions = customProductTypeOptions || (showAutomaticProductType ? buildingSetupProductTypeOptions : defaultProductTypeOptions);
  const rewardVisibilityOptions = customRewardVisibilityOptions || (showAutomaticProductType ? buildingSetupRewardVisibilityOptions : defaultRewardVisibilityOptions);

  const getProductTypeLabel = (value) => {
    if (showAutomaticProductType) {
      switch (value) {
        case "automatic": return t("buildingSetupChanges.automaticSelection");
        case "apartment_buildings": return t("buildingSetupChanges.offerForApartmentBuildings");
        case "commercial_spaces": return t("buildingSetupChanges.offerForCommercialSpaces");
        default: return value;
      }
    } else {
      return t(`partners.productTypes.${value}`);
    }
  };

  const getRewardVisibilityLabel = (value) => {
    if (showAutomaticProductType) {
      switch (value) {
        case "total": return t("buildingSetupChanges.createPdfWithTotalReward");
        case "separate": return t("buildingSetupChanges.createPdfWithSeparateReward");
        case "none": return t("buildingSetupChanges.createPdfWithoutReward");
        case "with_substitute_reward": return t("buildingSetupChanges.withSubstituteReward");
        default: return value;
      }
    } else {
      return t(`schedules.${value === 'show' ? 'showReward' : 'hideReward'}`);
    }
  };

  return (
    <>
      {showProductTypeSection && (
        <div>
          <h4>{t("buildingSetupChanges.selectProductType")}</h4>
          <Radio.Group
            className="vertical-radio-group"
            onChange={(e) => setProductType(e.target.value)}
            value={productType}
          >
            {productTypeOptions.map((option) => (
              <Radio key={option} value={option}>
                {getProductTypeLabel(option)}
              </Radio>
            ))}
          </Radio.Group>
        </div>
      )}
      
      {showRewardVisibilitySection && (
        <div style={{ marginTop: showProductTypeSection ? 16 : 0 }}>
          <h4>{t("buildingSetupChanges.selectRewardDisplay")}</h4>
          <Radio.Group
            className="vertical-radio-group"
            onChange={(e) => setRewardVisibility(e.target.value)}
            value={rewardVisibility}
          >
            {rewardVisibilityOptions.map((option) => (
              <Radio key={option} value={option}>
                {getRewardVisibilityLabel(option)}
              </Radio>
            ))}
          </Radio.Group>
        </div>
      )}
      
      {showLanguageSection && (
        <div style={{ marginTop: (showProductTypeSection || showRewardVisibilitySection) ? 16 : 0 }}>
          <LanguageSelect
            value={selectedLanguage}
            showAutomatic={showAutomaticLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
          />
        </div>
      )}

      {showStreetNumberSection && (
        <div style={{ marginTop: 16 }}>
          <HideStreetNumberSelect
            value={hideStreetNumber}
            onChange={(e) => setHideStreetNumber(e.target.value)}
          />
        </div>
      )}

      {showAdditionalPDFSection && (
        <div style={{ marginTop: 16 }}>
          <AdditionalPDFSelect
            value={additionalPDF}
            onChange={(e) => setAdditionalPDF(e.target.value)}
          />
        </div>
      )}
    </>
  );
};

export default PdfSettingsForm; 